import React, { useState } from 'react';

import A from 'wanderu-component-react-router-link';
import {
    Checkbox,
    Lightbox,
    Modal,
} from 'wanderu-components';
import { tripSummaryActions } from 'wanderu-ducks-search';
import { useAppDispatch } from 'wanderu-shellapp';

import Events from './events/PassengerModalEvents';
import {
    TRAVELER_TYPE,
    WHEELCHAIR_TYPE,
} from '../../../../constants';

import styles from './PassengerModal.scss';

import type {
    PassengerModalProps,
} from './../types';
import { localize } from 'wanderu-i18n';

const PassengerModal = ({
    itineraryId = '',
    maxSeats = 1,
    maxWheelchairs = 0,
    minSeats = 1,
    minWheelchairs = 0,
    nonAdaCarriers = [],
    requestedWheelchairs = 0,
    seats = 1,
    t,
    togglePassengerModal,
    wheelchairs = 0,
}: PassengerModalProps) => {
    const [showWheelchair, setShowWheelchair] = useState(requestedWheelchairs > 0);

    const dispatch = useAppDispatch();

    const setTravelerInfo = (info: { seats?: number; wheelchairs?: number }) => {
        dispatch(tripSummaryActions.setTravelerInfo(info));
    };

    const onSeatChange = (newSeats: number, { increase }: { increase: boolean }) => {
        Events.updateTravelerButtonClicked({ increase, itineraryId, travelers: newSeats });
    };

    const onWheelchairChange = (newWheelchairs: number, { increase }: { increase: boolean }) => {
        Events.updateWheelchairSeatButtonClicked({
            increase,
            itineraryId,
            wheelchairs: newWheelchairs,
        });
    };

    const increaseTravelers = () => {
        const newSeats = seats + 1;
        setTravelerInfo?.({ seats: newSeats });
        onSeatChange(newSeats, { increase: true });
    };

    const decreaseTravelers = () => {
        const newSeats = seats - 1;
        setTravelerInfo?.({ seats: newSeats });
        onSeatChange(newSeats, { increase: false });
    };

    const increaseWheelchairs = () => {
        if (wheelchairs >= seats) {
            return;
        }

        const newWheelchairs = wheelchairs + 1;
        setTravelerInfo?.({ wheelchairs: newWheelchairs });
        onWheelchairChange(newWheelchairs, { increase: true });
    };

    const decreaseWheelchairs = () => {
        if (wheelchairs === 0) {
            return;
        }

        const newWheelchairs = wheelchairs - 1;
        setTravelerInfo?.({ wheelchairs: wheelchairs - 1 });
        onWheelchairChange(newWheelchairs, { increase: false });
    };

    const resetWheelchairs = () => {
        if (wheelchairs === 0) {
            return;
        }

        setTravelerInfo?.({ wheelchairs: 0 });
    };

    const toggleWheelchair = (isActive: boolean) => {
        if (isActive && wheelchairs === 0 && maxWheelchairs > 0) {
            increaseWheelchairs();
        }

        if (!isActive && wheelchairs !== 0) {
            resetWheelchairs();
        }

        setShowWheelchair(isActive);
    };

    const decreaseTravelersAndWheelchairs = () => {
        const newSeats = seats - 1;
        setTravelerInfo?.({
            seats: newSeats,
            wheelchairs: wheelchairs - 1,
        });
        onSeatChange(newSeats, { increase: false });
    };

    const decreasePassengerCount = (e: React.MouseEvent<HTMLButtonElement>) => {
        const { type } = (e.target as HTMLButtonElement).dataset;

        if (type === WHEELCHAIR_TYPE) {
            return decreaseWheelchairs();
        }

        if (seats === wheelchairs) {
            return decreaseTravelersAndWheelchairs();
        }

        return decreaseTravelers();
    };

    const increasePassengerCount = (e: React.MouseEvent<HTMLButtonElement>) => {
        const { type } = (e.target as HTMLButtonElement).dataset;

        if (type === WHEELCHAIR_TYPE) {
            return increaseWheelchairs();
        }

        return increaseTravelers();
    };

    const renderTravelerRow = (type: string, count: number, max: number, min: number) => {
        const disableAdd = count === max;
        const disableSubtract = count === min;

        return (
            <div className={styles.travelerRow}>
                <span className={styles.travelerType}>
                    {t<string>(type, { count })}
                </span>

                <div className={styles.counter}>
                    {/*
                        uses the native button element because our component
                        version won’t handle e.target.dataset (see increasePassengerCount)
                    */}
                    <button
                        aria-label={t('less')}
                        className={styles.minusIcon}
                        data-type={type}
                        disabled={disableSubtract}
                        onClick={decreasePassengerCount}
                    />

                    <span className={`count ${type}`}>
                        {count}
                    </span>

                    <button
                        aria-label={t('more')}
                        className={styles.plusIcon}
                        data-type={type}
                        disabled={disableAdd}
                        onClick={increasePassengerCount}
                    />
                </div>
            </div>
        );
    };

    const renderSeatRow = () => {
        return renderTravelerRow(TRAVELER_TYPE, seats, maxSeats, minSeats);
    };

    const renderWheelchairRow = () => {
        const max = Math.min(seats, maxWheelchairs);
        return renderTravelerRow(WHEELCHAIR_TYPE, wheelchairs, max, minWheelchairs);
    };

    let carrierNames = nonAdaCarriers[0];

    if (nonAdaCarriers.length > 1) {
        const lastCarrier = nonAdaCarriers.pop();
        carrierNames = `${nonAdaCarriers.join(', ')} ${t('or')} ${lastCarrier}`;
    }

    return (
        <Modal>
            <Lightbox
                innerClassName={styles.dialog}
                onHide={togglePassengerModal}
            >
                <div className={styles.container}>
                    {renderSeatRow()}
                    {showWheelchair && renderWheelchairRow()}
                    <div className={styles.wheelchairCheckbox}>
                        <Checkbox
                            checkboxClassName={styles.checkbox}
                            checked={showWheelchair}
                            labelText={t('need-wheelchair')}
                            onChange={toggleWheelchair}
                        />
                    </div>
                    {showWheelchair && !!nonAdaCarriers.length && (
                        <div className={styles.wheelchairDisclaimer}>
                            {t<string>('wheelchair-disclaimer', {
                                carrierName: (
                                    <A
                                        href="//www.wanderu.com/carrier/"
                                        key="wheelchairDisclaimer"
                                        native
                                    >
                                        {carrierNames}
                                    </A>
                                ),
                            })}
                        </div>
                    )}
                </div>
            </Lightbox>
        </Modal>
    );
};

export { PassengerModal };

export default localize(PassengerModal, ['t'], 'baseApp');
